class SideDrawerOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');

    button?.addEventListener('click', () => {
      const drawerSelector = this.getAttribute('data-side-drawer');
      const modalSelector = `#Modal-${drawerSelector.replace('#', '')}`;
      const drawer = document.querySelector(drawerSelector);
      const modal = theme.ensureLazyModalMounted?.(modalSelector) || document.querySelector(modalSelector);
      drawer?.open(button);
      modal?.show(button);
    });
  }
}

customElements.define('side-drawer-opener', SideDrawerOpener);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('[id^="Drawer-Overlay-"]')?.addEventListener('click', this.close.bind(this));
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => this.classList.add('active'));

    this.handleTransition(true, 'drawer--opening', 'drawer--open');
    this.trapFocus();
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (this.activeElement) theme.removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.handleTransition(false, 'drawer--closing');
  }

  handleTransition(checkOpen, startClass, endClass = '') {
    const isDrawer = this.querySelector('.side-drawer:not(.popup__inner)');
    if (!isDrawer) return;

    this.addEventListener('transitionstart', () => {
      document.body.classList.add(startClass);
      checkOpen ? document.body.classList.remove('drawer--open', 'drawer--closing') : document.body.classList.remove('drawer--open', 'drawer--opening');
    }, { once: true });

    this.addEventListener('transitionend', () => {
      checkOpen ? document.body.classList.remove(startClass, 'drawer--opening', 'drawer--closing') : document.body.classList.remove('drawer--closing', 'drawer--opening', 'drawer--open');
      if (endClass) document.body.classList.add(endClass);
    }, { once: true });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  trapFocus() {
    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.querySelector('.drawer__inner, .popup__inner');
      const focusElement = this.querySelector('.button-close, .search__input, .popup__input');
      theme.trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
  }
}

customElements.define('side-drawer', SideDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('#Modal-Overlay')?.addEventListener('click', this.hide.bind(this));
    this.contentWrapper = this.querySelector('[data-modal-content-wrapper]');
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    this.animateIn();
    if (popup) popup.loadContent();
    this.trapFocus();
    theme.pauseAllMedia();
  }

  animateIn() {
    gsap.fromTo(this.contentWrapper,
      { opacity: 0, clipPath: 'inset(0 0 100% 0)', scale: 0.9, willChange: 'opacity, clip-path, transform' },
      { opacity: 1, clipPath: 'inset(0 0 0% 0)', scale: 1, duration: 0.6, ease: "power3.inOut" }
    );
  }

  animateOut() {
    gsap.to(this.contentWrapper,
      { opacity: 0, clipPath: 'inset(100% 0px 0px 0px)', scale: 0.9, willChange: 'opacity, clip-path, transform', duration: 0.6, ease: "power3.inOut" },
    );
  }

  hide() {
    this.animateOut();
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    theme.removeTrapFocus(this.openedBy);
    theme.pauseAllMedia();

    if(document.body.classList.contains('edit-cart-show')) {
      document.body.classList.remove('edit-cart-show');
    }
  }

  trapFocus() {
    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.querySelector('.drawer__inner, .popup__inner');
      const focusElement = this.querySelector('[role="dialog"], .popup__inner');
      theme.trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
  }
}

customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const targetSelector = this.getAttribute('data-modal');
      const modalSelector = this.getAttribute('data-modal-toggle');

      if (modalSelector?.startsWith('#')) {
        theme.ensureLazyModalMounted?.(modalSelector);
      }
      if (targetSelector?.startsWith('#')) {
        theme.ensureLazyModalByTarget?.(targetSelector);
      }

      const modal = document.querySelector(targetSelector);

      if (modal) modal.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class CollapsibleDetails extends HTMLDetailsElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!theme.media.isTouch || Shopify.designMode) {
      this.init();
    } else {
      theme.initWhenVisible(this.init());
    }
  }

  init() {
    this._isOpen = this.hasAttribute('open');
    this.summary = this.querySelector('summary');
    this.content = this.summary.nextElementSibling;
    this.setAttribute('aria-expanded', this._isOpen ? 'true' : 'false');
    this.summary?.addEventListener('click', this._toggleOpen.bind(this));

    if (this.hasAttribute('collapsible-mobile')) {
      this.resizeHandler();
      window.addEventListener('resize', this.resizeHandler.bind(this));
      document.addEventListener('unmatchSmall', this.resizeHandler.bind(this));
    }

    if (Shopify.designMode && this.hasAttribute('check-shopify-design-mode')) {
      this.addEventListener('shopify:block:select', () => this.isOpen = true);
      this.addEventListener('shopify:block:deselect', () => this.isOpen = false);
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    if (value !== this._isOpen) {
      this._isOpen = value;

      if (this.isConnected) {
        this._animate(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
    this.setAttribute('aria-expanded', value ? 'true' : 'false');
  }

  _toggleOpen(event) {
    event.preventDefault();
    this.isOpen = !this.isOpen;
  }

  close() {
    this._isOpen = false;
    this._animate(false);
  }

  resizeHandler() {
    if (window.matchMedia('(max-width: 749px)').matches) {
      if (this.isOpen) {
        this._isOpen = false;
        this.removeAttribute('open');
        this.setAttribute('aria-expanded', 'false');
      }
    } else {
      this._isOpen = true;
      this.setAttribute('open', '');
      this.setAttribute('aria-expanded', 'true');
    }
  }

  async _animate(open) {
    this.style.overflow = 'hidden';
    if (open) {
      this.setAttribute('open', '');

      await gsap.timeline()
        .fromTo(this,
          { height: `${this.summary.clientHeight}px` },
          {
            height: `${this.scrollHeight}px`,
            duration: 0.45,
            ease: "cubic-bezier(0.7, 0, 0.3, 1)",
          }
        )
        .fromTo(this.content,
          { opacity: 0, y: -16 },
          { opacity: 1, y: 0, duration: 0.25 },
          "-=0.3"
        )
        .set(this.content, { clearProps: "all" }).finished;
    } else {
      await gsap.timeline()
        .to(this.content, {
          opacity: 0,
          y: -16,
          duration: 0.15
        })
        .to(this, {
          height: `${this.summary.clientHeight}px`,
          duration: 0.35,
          ease: "cubic-bezier(0.7, 0, 0.3, 1)"
        }, "<");

      this.removeAttribute('open');
    }

    this.style.height = 'auto';
    this.style.overflow = 'visible';
  }
}

function initCollapsibleDetailsFallback() {
  const initialize = (detailsElement) => {
    if (!detailsElement || detailsElement.dataset.collapsibleDetailsInit === 'true') return;
    detailsElement.dataset.collapsibleDetailsInit = 'true';

    const summary = detailsElement.querySelector('summary');
    if (!summary) return;

    const applyResponsiveState = () => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (isMobile) {
        detailsElement.open = false;
        detailsElement.removeAttribute('open');
        detailsElement.setAttribute('aria-expanded', 'false');
      } else {
        detailsElement.open = true;
        detailsElement.setAttribute('open', '');
        detailsElement.setAttribute('aria-expanded', 'true');
      }
    };

    const toggleByClick = (event) => {
      event.preventDefault();
      const nextOpen = !detailsElement.open;
      detailsElement.open = nextOpen;
      if (nextOpen) {
        detailsElement.setAttribute('open', '');
      } else {
        detailsElement.removeAttribute('open');
      }
      detailsElement.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
    };

    if (detailsElement.hasAttribute('collapsible-mobile')) {
      applyResponsiveState();
      window.addEventListener('resize', applyResponsiveState);
      document.addEventListener('unmatchSmall', applyResponsiveState);
    }

    summary.addEventListener('click', toggleByClick);
  };

  document.querySelectorAll('details[is="collapsible-details"]').forEach(initialize);
}

// try {
//   customElements.define('collapsible-details', CollapsibleDetails, { extends: 'details' });
// } catch (error) {
//   // Safari iOS and some environments do not support customized built-in elements.
// }

const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
const isAndroid = /Android/.test(navigator.userAgent);

if (isIOS || isAndroid) {
  initCollapsibleDetailsFallback();
} else {
  customElements.define('collapsible-details', CollapsibleDetails, { extends: 'details' });
}

class DropdownDetails extends HTMLDetailsElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!theme.media.isTouch || Shopify.designMode) {
      this.init();
    } else {
      theme.initWhenVisible(this.init());
    }
  }

  init() {
    this.dataset.dropdownDetailsInit = 'true';
    this.mqlDesktop = window.matchMedia('(min-width: 1025px)');
    this._isOpen = this.hasAttribute('open') && this.getAttribute('open') === 'true';
    this.summary = this.querySelector('summary');
    this.content = this.summary.nextElementSibling;

    if (!this) return;
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', theme.onKeyUpEscape);
    this.setAttribute('open', 'false');
    this.summary?.addEventListener('click', this._toggleOpen.bind(this));

    if (this.mqlDesktop.matches && this.getAttribute('activate-event') !== 'click') {
      const hoverEvents = ['focusin', 'mouseenter', 'mouseleave'];
      hoverEvents.forEach(event => this.addEventListener(event, this._hoverOpen.bind(this)));
    }

    if (Shopify.designMode && this.hasAttribute('check-shopify-design-mode')) {
      this.addEventListener('shopify:block:select', () => {
        this.isOpen = true;
        this.content.classList.add('active');
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.isOpen = false;
        this.content.classList.remove('active');
      });
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') this.summary?.setAttribute('aria-expanded', newValue === 'true');
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    if (value !== this._isOpen) {
      this._isOpen = value;
      if (this.isConnected) {
        this._animate(value);
      } else {
        this.setAttribute('open', value ? 'true' : 'false');
      }
    }
    this.summary.setAttribute('aria-expanded', value ? 'true' : 'false');
  }

  onFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.content.classList.remove('active');
      this.isOpen = false;
    }
  }

  _toggleOpen(event) {
    event.preventDefault();
    if (!theme.media.isTouch && this.getAttribute('activate-event') === 'hover' && this.summary.hasAttribute('data-link') && this.summary.getAttribute('data-link').length > 0) {
      window.location.href = this.summary.getAttribute('data-link');
    }
    else {
      // this.open = !this.open;
      this.isOpen = !this.isOpen;
      this.content.classList.toggle('active');
    }
    // this.toggleAttribute('open', this.isOpen);
  }

  _hoverOpen(event) {
    const value = event.type === 'mouseenter' || event.type === 'focusin';
    this.isOpen = value;
    this.content.classList.toggle('active', value);
    // if (this.closest('header-menu')) this.closest('.header-wrapper').preventHide = value;
  }

  close() {
    this.isOpen = false;
    this.content.classList.remove('active');
    this.setAttribute('open', 'false');
  }

  async _animate(open) {
    if (open) {
      this.setAttribute('open', 'true');
      if (this.content.classList.contains('header__submenu')) {
        await gsap.fromTo(
          this.content.querySelectorAll(':scope > li'),
          { opacity: 0, x: 10 },
          { opacity: 1, x: 0, delay: 0.4, duration: 0.25, stagger: 0.1 }
        );
      } else {
        await gsap.fromTo(
          this.content,
          { opacity: 0, visibility: 'hidden', clipPath: 'inset(0 0 100% 0)', willChange: 'opacity, visibility, clip-path' },
          { opacity: 1, visibility: 'visible', clipPath: 'inset(0 0 0% 0)', duration: 0.15, overwrite: 'auto' }
        );

      }
    } else {
      if (this.content.classList.contains('header__submenu')) return this.setAttribute('open', 'false');
      await gsap.timeline()
        .to(
          this.content,
          { opacity: 0, visibility: 'hidden', clipPath: 'inset(100% 0px 0px 0px)', duration: 0.15, overwrite: 'auto' }
        )
        .set(this.content, { clearProps: "all" });

      this.setAttribute('open', 'false');
    }
  }
}

function initDropdownDetailsFallback() {
  const initialize = (detailsElement) => {
    if (!detailsElement || detailsElement.dataset.dropdownDetailsInit === 'true') return;

    detailsElement.dataset.dropdownDetailsInit = 'true';
    const button = detailsElement.querySelector('summary');
    const dropdown = detailsElement.querySelector('summary').nextElementSibling;
    const mqlDesktop = window.matchMedia('(min-width: 1025px)');
    const activateEvent = detailsElement.getAttribute('activate-event');
    const isHoverActivated = activateEvent !== 'click';

    if (!button || !dropdown) return;

    const openDropdown = () => {
      detailsElement.open = true;
      detailsElement.setAttribute('open', 'true');
      button.setAttribute('aria-expanded', 'true');
      dropdown.classList.add('active');
    };

    const closeDropdown = () => {
      detailsElement.open = false;
      detailsElement.setAttribute('open', 'false');
      button.setAttribute('aria-expanded', 'false');
      dropdown.classList.remove('active');
    };

    const handleToggleClick = (event) => {
      event.preventDefault();

      const link = button.getAttribute('data-link');
      if (!theme.media.isTouch && isHoverActivated && link && link.length > 0) {
        window.location.href = link;
        return;
      }

      if (detailsElement.getAttribute('open') === 'true') {
        closeDropdown();
      } else {
        openDropdown();
      }
    };

    const handleFocusOut = (event) => {
      if (event.relatedTarget && !detailsElement.contains(event.relatedTarget)) {
        closeDropdown();
      }
    };

    closeDropdown();
    button.addEventListener('click', handleToggleClick);
    detailsElement.addEventListener('focusout', handleFocusOut);
    detailsElement.addEventListener('keyup', theme.onKeyUpEscape);

    if (mqlDesktop.matches && isHoverActivated) {
      detailsElement.addEventListener('focusin', openDropdown);
      detailsElement.addEventListener('mouseenter', openDropdown);
      detailsElement.addEventListener('mouseleave', closeDropdown);
    }
  };

  document.querySelectorAll('details[is="dropdown-details"]').forEach(initialize);
}

// try {
//   customElements.define('dropdown-details', DropdownDetails, { extends: 'details' });
// } catch (error) {
//   // Safari iOS and some environments do not support customized built-in elements.
// }

if (isIOS || isAndroid) {
  initDropdownDetailsFallback();
} else {
  customElements.define('dropdown-details', DropdownDetails, { extends: 'details' });
}

// initDropdownDetailsFallback();